/**
 * Gives asset reviewers a cancellable draft for choosing shared facts across
 * card and table views without mutating saved preferences prematurely.
 */
import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import {
  ASSET_VIEW_FIELDS,
  DEFAULT_PREFERENCES,
  type AssetViewFieldId,
  type AssetViewPreferences,
} from 'src/components/assets/assetViewFields';
import { isFeatureEnabled } from 'src/feature-flags';

const fieldsetResetStyles = css({
  border: 0,
  margin: 0,
  padding: 0,
  '& legend': {
    padding: 0,
  },
});

export interface AssetViewPreferencesModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly preferences: AssetViewPreferences;
  readonly onSave: (next: AssetViewPreferences) => void;
}

export const AssetViewPreferencesModal: React.FC<AssetViewPreferencesModalProps> = ({
  onClose,
  onSave,
  open,
  preferences,
}): React.ReactElement => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<AssetViewPreferences>(preferences);
  const wasOpen = useRef(open);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraft(preferences);
    }

    wasOpen.current = open;
  }, [open, preferences]);

  const handleFieldChange = (id: AssetViewFieldId, checked: boolean): void => {
    setDraft((current) => ({
      fields: { ...current.fields, [id]: checked },
    }));
  };

  const handleUpdate = (): void => {
    onSave(draft);
    onClose();
  };

  return (
    <Modal
      bodySpacingInset="none"
      data-testid="asset-view-preferences-modal"
      open={open}
      title={t('asset_view_preferences')}
      width={480}
      onClose={onClose}
    >
      <Column spacing="450" spacingInset="500">
        <Column
          className={fieldsetResetStyles}
          spacing="300"
          tag="fieldset"
        >
          <Text tag="legend" type="h100">
            {t('asset_view_preferences_information_shown')}
          </Text>
          {ASSET_VIEW_FIELDS.map((field) => {
            if (
              field.featureFlag !== undefined &&
              !isFeatureEnabled(field.featureFlag)
            ) {
              return null;
            }

            return (
              <Checkbox
                checked={field.alwaysVisible === true || draft.fields[field.id]}
                data-testid={`asset-view-preferences-field-${field.id}`}
                disabled={field.alwaysVisible === true}
                key={field.id}
                name="asset-view-fields"
                value={field.id}
                onChange={(checked: boolean) => {
                  handleFieldChange(field.id, checked);
                }}
              >
                {t(field.labelKey)}
              </Checkbox>
            );
          })}
        </Column>
        <Button
          data-testid="asset-view-preferences-reset"
          type="link"
          onClick={() => {
            setDraft(DEFAULT_PREFERENCES);
          }}
        >
          {t('asset_view_preferences_reset')}
        </Button>
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="200" widths="fit">
          <Button
            data-testid="asset-view-preferences-cancel"
            type="secondary"
            onClick={onClose}
          >
            {t('buttons_cancel')}
          </Button>
          <Button
            data-testid="asset-view-preferences-update"
            type="primary"
            onClick={handleUpdate}
          >
            {t('asset_view_preferences_update')}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
